import React, { useRef, useState, useEffect } from 'react';
import {
  HStack, Text, Box, IconButton, VStack,
  Heading, Button,
} from '@chakra-ui/react';
import { useColorModeValue } from './chakra-ui/color-mode';
import { FONTS } from '../constants/fonts';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { scrollToSection } from './core/scroll-to-section';


const specialties = [
  {
    icon: '📷',
    label: 'Portraits',
    description: 'Capture your essence with professional portrait photography.'
  },
  {
    icon: '💒',
    label: 'Weddings',
    description: 'Celebrate your special day with stunning wedding photography.'
  },
  {
    icon: '🎉',
    label: 'Events',
    description: 'Document memorable moments from corporate to social events.'
  },
  {
    icon: '🏠',
    label: 'Housewarming',
    description: 'Mark new beginnings with beautiful housewarming photos.'
  },
  {
    icon: '🎂',
    label: 'Birthday',
    description: 'Preserve birthday celebrations with candid and posed shots.'
  },
  {
    icon: '🤰',
    label: 'Seemantham',
    description: 'Celebrate traditional rituals with elegant photography.'
  },
  {
    icon: '🍼',
    label: 'Cradle Ceremony',
    description: 'Capture sacred moments of new beginnings.'
  },
  {
    icon: '👗',
    label: 'Half Saree',
    description: 'Document this significant cultural milestone beautifully.'
  },
  {
    icon: '👶',
    label: 'Maternity',
    description: 'Celebrate the beauty of motherhood with artistic maternity shots.'
  },
  {
    icon: '🌄',
    label: 'Landscape',
    description: 'Showcase nature\'s beauty through breathtaking landscape photography.'
  }
];

function ServicesSection() {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const capsuleBg = useColorModeValue(
    'rgba(255, 255, 255, 0.3)',
    'rgba(20, 20, 25, 0.3)'
  );
  const capsuleBorder = useColorModeValue(
    'rgba(244, 136, 91, 0.15)',
    'rgba(255, 255, 255, 0.08)'
  );
  const itemBg = useColorModeValue('whiteAlpha.600', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 20);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 20);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      const timeout = setTimeout(checkScroll, 100);
      window.addEventListener('resize', checkScroll);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
        clearTimeout(timeout);
      };
    }
  }, []);

  const scrollBy = (distance) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: distance, behavior: 'smooth' });
    }
  };

  return (
    <VStack spacing={8} py={8} px={2} align="stretch">
      <VStack spacing={2} textAlign="center" mb={2}>
        <Heading size="2xl" letterSpacing="tight">Our Services</Heading>
        <Text fontSize="lg" color="gray.600" maxW="2xl" mx="auto">
          Discover our range of professional photography services for every occasion.
        </Text>
      </VStack>
      <Box
        w="full"
        display="flex"
        justifyContent="center"
        px={0}
      >
        <Box
          position="relative"
          w="98%"
          display="flex"
          alignItems="center"
        >
          {showLeftArrow && (
            <IconButton
              aria-label="Scroll Left"
              position="absolute"
              left="-12px"
              zIndex={12}
              size="sm"
              variant="ghost"
              borderRadius="full"
              bg={capsuleBg}
              backdropFilter="blur(8px)"
              onClick={() => scrollBy(-180)}
              _hover={{ bg: 'whiteAlpha.400' }}
            >
              <FaChevronLeft size="14px" color="gray.400" />
            </IconButton>
          )}
          <Box
            ref={scrollRef}
            overflowX="auto"
            overflowY="hidden"
            display="flex"
            justifyContent="flex-start"
            css={{
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              scrollSnapType: 'x mandatory'
            }}
            bg={capsuleBg}
            backdropFilter="blur(20px) saturate(160%)"
            border="1px solid"
            borderColor={capsuleBorder}
            borderRadius="2xl"
            p={2}
            w="full"
          >
            <HStack spacing={2} flexShrink={0}>
              {specialties.map((item) => (
                <Box
                  key={item.label}
                  bg={itemBg}
                  borderRadius="2xl"
                  px={4}
                  py={3}
                  minW="180px"
                  maxW="220px"
                  boxShadow="sm"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  scrollSnapAlign="center"
                  mx={1}
                  position="relative"
                  overflow="hidden"
                  transition="box-shadow 0.2s, transform 0.2s"
                  _hover={{ boxShadow: 'md', transform: 'translateY(-4px)' }}
                >
                  {/* Emoji background */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    w="full"
                    h="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={0}
                    pointerEvents="none"
                  >
                    <Text
                      fontSize="7xl"
                      opacity={0.13}
                      lineHeight={1}
                      userSelect="none"
                    >
                      {item.icon}
                    </Text>
                  </Box>
                  {/* Card content */}
                  <Box position="relative" zIndex={1} w="full">
                    <Text
                      fontWeight="bold"
                      fontSize="md"
                      fontFamily={FONTS.heading}
                      color={textColor}
                      letterSpacing="wide"
                      textTransform="uppercase"
                      mb={1}
                      textAlign="center"
                    >
                      {item.label}
                    </Text>
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      {item.description}
                    </Text>
                  </Box>
                </Box>
              ))}
              {/* And More card */}
              <Button
                as={RouterLink}
                to="/#contact"
                onClick={e => {
                  e.preventDefault();
                  scrollToSection('contact');
                }}
                bg={itemBg}
                borderRadius="xl"
                px={4}
                py={3}
                minW="140px"
                maxW="180px"
                boxShadow="sm"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                scrollSnapAlign="center"
                mx={1}
                border="1px dashed"
                borderColor={capsuleBorder}
                color={textColor}
                opacity={0.85}
                fontWeight="bold"
                fontSize="md"
                fontFamily={FONTS.heading}
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ bg: itemBg, opacity: 1, boxShadow: 'md', borderColor: textColor }}
                transition="all 0.2s"
              >
                <Text fontSize="2xl" mb={1}>
                  ...
                </Text>
                And More
                <Text fontSize="xs" color="gray.400" textAlign="center" mt={1} fontWeight="normal" textTransform="none">
                  Ask us for custom services!
                </Text>
              </Button>
            </HStack>
          </Box>
          {showRightArrow && (
            <IconButton
              aria-label="Scroll Right"
              position="absolute"
              right="-12px"
              zIndex={12}
              size="sm"
              variant="ghost"
              borderRadius="full"
              bg={capsuleBg}
              backdropFilter="blur(8px)"
              onClick={() => scrollBy(180)}
              _hover={{ bg: 'whiteAlpha.400' }}
            >
              <FaChevronRight size="14px" color="gray.400" />
            </IconButton>
          )}
        </Box>
      </Box>
    </VStack>
  );
}

export default ServicesSection;
